import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles, Server as ServerIcon, X } from "lucide-react";

const CATEGORIES = [
  "Hosting", "Minecraft", "Payments", "Billing", "Store Orders",
  "Technical Support", "Bugs", "Feature Requests", "Partnerships",
  "Refunds", "Appeals", "Abuse Reports", "Account Recovery", "General Support",
];
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];
const DEPARTMENTS = ["General", "Hosting", "Minecraft", "Billing", "Abuse", "Technical"];

export default function NewTicket() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState({ categories: CATEGORIES, priorities: PRIORITIES, departments: DEPARTMENTS });
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("General Support");
  const [priority, setPriority] = useState("normal");
  const [department, setDepartment] = useState("General");
  const [loading, setLoading] = useState(false);
  const [attachedServer, setAttachedServer] = useState(null);

  useEffect(() => {
    const raw = params.get("server");
    if (raw) {
      try {
        const s = JSON.parse(raw);
        setAttachedServer(s);
        setCategory("Hosting");
        setSubject(`Issue with server ${s.name}`);
      } catch {}
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tickets", {
        subject, description, category, priority, department,
        tags: attachedServer ? ["pterodactyl"] : [],
        attached_server: attachedServer,
      });
      toast.success(`Ticket ${data.ticket_number} created`);
      navigate(`/tickets/${data.ticket_id}`);
    } catch (e2) {
      toast.error(extractError(e2));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl">
      <header className="mb-8">
        <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// New ticket</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Create a support ticket</h1>
        <p className="text-sm text-muted-foreground mt-1">Tell us what happened — the more precise, the faster we can help.</p>
      </header>
      <form onSubmit={submit} className="space-y-5">
        {attachedServer && (
          <div className="p-4 rounded-lg border border-primary/40 bg-primary/5 flex items-start gap-3" data-testid="attached-server-card">
            <ServerIcon className="h-4 w-4 mt-0.5 text-primary" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary">Attached server</div>
              <div className="font-medium text-sm">{attachedServer.name} <span className="font-mono text-xs text-muted-foreground">({attachedServer.identifier})</span></div>
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => setAttachedServer(null)}><X className="h-4 w-4" /></Button>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Subject</Label>
          <Input data-testid="ticket-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required minLength={4} maxLength={200} placeholder="Short summary of the issue" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Category</Label>
            <Select key={`cat-${category}`} value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="ticket-category"><SelectValue placeholder={category} /></SelectTrigger>
              <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="ticket-priority"><span>{priority}</span></SelectTrigger>
              <SelectContent>{meta.priorities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Department</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger data-testid="ticket-department"><span>{department}</span></SelectTrigger>
              <SelectContent>{meta.departments.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Description</Label>
          <Textarea data-testid="ticket-description" value={description} onChange={(e) => setDescription(e.target.value)} required rows={8} placeholder="Describe the issue, logs, steps to reproduce…" />
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="ticket-submit" type="submit" disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Create ticket
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
